// Credit Investigation — field capture side (agency app).
//
// Agents only ever see applicants they are assigned to investigate; that is
// enforced by RLS on loan_applicants (ci_officer_id = auth.uid()), not here.
//
// Capture is deliberately NOT a form: the 11 checklist questions are prompts,
// and everything the officer learns goes into one free-text field in whatever
// language they work in. Structure is extracted later by the AI.

import { supabase } from '../../lib/supabase';

export interface CiQuestionItem {
  key: string;
  order: number;
  asks: 'neighbor' | 'applicant';
  expects: 'yes_no' | 'text';
  label: string;
  label_en: string;
}

export interface CiQuestionTemplate {
  id: string;
  version: number;
  items: CiQuestionItem[];
  is_active: boolean;
}

export interface CiInvestigation {
  id: string;
  applicant_id: string;
  ci_officer_id?: string | null;
  assigned_by?: string | null;
  assigned_at?: string | null;
  visit_date?: string | null;
  template_version?: number | null;
  field_notes?: string | null;
  recommendation?: 'approved' | 'disapproved' | null;
  employment_observed?: 'contractual' | 'regular' | null;
  remarks?: string | null;
  status: 'assigned' | 'in_progress' | 'submitted';
  submitted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CiApplicant {
  id: string;
  full_name?: string;
  first_name: string;
  last_name: string;
  mobile_number?: string | null;
  current_address?: string | null;
  permanent_address?: string | null;
  requested_amount?: number | null;
  loan_purpose?: string | null;
  product_name?: string | null;
  employer_name?: string | null;
  photo_path?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  location_accuracy_m?: number | null;
}

const APPLICANT_FIELDS =
  'id, full_name, first_name, last_name, mobile_number, current_address, permanent_address, ' +
  'requested_amount, loan_purpose, product_name, employer_name, photo_path, latitude, longitude, ' +
  'location_accuracy_m';

export interface Coords {
  latitude: number;
  longitude: number;
  accuracy?: number | null;
}

/**
 * The officer is standing at the house — this is the most accurate pin the
 * business will ever get for a rural Philippine address, far better than
 * geocoding the address text.
 */
export async function pinApplicantLocation(params: {
  applicantId: string;
  coords: Coords;
  pinnedBy: string;
}): Promise<void> {
  const { latitude, longitude, accuracy } = params.coords;
  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
    throw new Error('Invalid latitude.');
  }
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    throw new Error('Invalid longitude.');
  }
  const { error } = await supabase
    .from('loan_applicants')
    .update({
      latitude,
      longitude,
      location_accuracy_m: accuracy ?? null,
      location_pinned_at: new Date().toISOString(),
      location_pinned_by: params.pinnedBy,
    })
    .eq('id', params.applicantId);
  if (error) throw error;
}

export function readCurrentPosition(): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('This device does not support location.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: Number(pos.coords.latitude.toFixed(6)),
        longitude: Number(pos.coords.longitude.toFixed(6)),
        accuracy: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null,
      }),
      (err) => {
        const messages: Record<number, string> = {
          1: 'Location permission denied. Allow location access for this site and try again.',
          2: 'Location unavailable. Step outside for a clearer signal and try again.',
          3: 'Timed out getting a fix. Try again.',
        };
        reject(new Error(messages[err.code] || 'Could not get your location.'));
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
}

/** Turn-by-turn directions — what the officer needs to find the house. */
export function googleMapsDirectionsUrl(a?: CiApplicant | null): string | null {
  if (!a) return null;
  if (a.latitude != null && a.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${a.latitude},${a.longitude}`;
  }
  if (a.current_address?.trim()) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a.current_address.trim())}`;
  }
  return null;
}

export interface CiQueueItem {
  investigation: CiInvestigation;
  applicant: CiApplicant | null;
}

export async function getActiveTemplate(): Promise<CiQuestionTemplate | null> {
  const { data, error } = await supabase
    .from('ci_question_templates')
    .select('*')
    .eq('is_active', true)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as CiQuestionTemplate) ?? null;
}

/**
 * Two queries rather than a PostgREST embed: the embedded form applies RLS to
 * the joined table in ways that are easy to get subtly wrong, and this is
 * clearer about what the agent is allowed to see.
 */
export async function listMyQueue(officerId: string): Promise<CiQueueItem[]> {
  const { data: invs, error } = await supabase
    .from('ci_investigations')
    .select('*')
    .eq('ci_officer_id', officerId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;

  const investigations = (invs || []) as CiInvestigation[];
  if (investigations.length === 0) return [];

  const ids = Array.from(new Set(investigations.map((i) => i.applicant_id)));
  const { data: apps, error: appErr } = await supabase
    .from('loan_applicants_view')
    .select(APPLICANT_FIELDS)
    .in('id', ids);
  if (appErr) throw appErr;

  const byId = new Map((apps || []).map((a: any) => [a.id, a as CiApplicant]));
  return investigations.map((investigation) => ({
    investigation,
    applicant: byId.get(investigation.applicant_id) ?? null,
  }));
}

export async function getInvestigation(id: string): Promise<CiQueueItem | null> {
  const { data, error } = await supabase
    .from('ci_investigations')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const investigation = data as CiInvestigation;
  const { data: app, error: appErr } = await supabase
    .from('loan_applicants_view')
    .select(APPLICANT_FIELDS)
    .eq('id', investigation.applicant_id)
    .maybeSingle();
  if (appErr) throw appErr;

  // APPLICANT_FIELDS is a runtime constant, so PostgREST's literal-string
  // type inference doesn't apply — cast through unknown.
  return { investigation, applicant: (app as unknown as CiApplicant) ?? null };
}

export async function saveInvestigation(
  id: string,
  patch: Partial<CiInvestigation>
): Promise<void> {
  const { id: _id, created_at, updated_at, ...clean } = patch as any;
  const { error } = await supabase.from('ci_investigations').update(clean).eq('id', id);
  if (error) throw error;
}

export async function submitInvestigation(
  investigationId: string,
  applicantId: string
): Promise<void> {
  const { error } = await supabase
    .from('ci_investigations')
    .update({ status: 'submitted', submitted_at: new Date().toISOString() })
    .eq('id', investigationId);
  if (error) throw error;

  // Move the applicant along the pipeline so the office sees it as ready.
  await supabase
    .from('loan_applicants')
    .update({ status: 'ci_submitted' })
    .eq('id', applicantId);
}

// ── Local draft ────────────────────────────────────────────────────────────
// Field work happens where signal is unreliable. Notes are mirrored to
// localStorage on every keystroke so a dropped connection mid-visit can't
// lose an hour of interviews.

const draftKey = (id: string) => `ci_draft_${id}`;

export function saveLocalDraft(investigationId: string, notes: string): void {
  try {
    localStorage.setItem(draftKey(investigationId), JSON.stringify({
      notes, at: new Date().toISOString(),
    }));
  } catch {
    // Storage full or unavailable — the DB save is the real path, not this.
  }
}

export function readLocalDraft(investigationId: string): { notes: string; at: string } | null {
  try {
    const raw = localStorage.getItem(draftKey(investigationId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearLocalDraft(investigationId: string): void {
  try {
    localStorage.removeItem(draftKey(investigationId));
  } catch {
    /* no-op */
  }
}
